import type { Edge, Node } from "@xyflow/react";
import {
    type BucketSelection,
    type BuildFacetGraphParams,
    type Cluster,
    type ExplodeFlow,
    type ExplodeNodeData,
    NodeKind,
    NodeRole,
    type OverlayData,
} from "../types";

const NODE_WIDTH = 200;
const COLUMN_GAP = 72;
const ROW_GAP = 132;
const ROOT_Y = 0;
const GRID_TOP = 130;
const MAX_COLUMNS = 8;
const TARGET_ASPECT = 3.6;
const OVERLAY_Z = 20;

const ZONE_ROWS = 2;
const ZONE_WIDTH = NODE_WIDTH + 120;
const ZONE_LEFT_OFFSET = NODE_WIDTH + 160;
const ZONE_TOP_OFFSET = -120;
const TITLE_SLOT_HEIGHT = 96;
const ZONE_ROW_GAP = 70;

const GHOST_COUNT = 4;
const GHOST_ZONE_SLOTS = 2;
export const OVERLAY_PAGE_SIZE = 25;
export const FIELD_FOCUS_TOP_VALUES = 8;
const STAGE_Z = 40;

const columnsForAspect = (count: number): number => {
    const cellAspect = ROW_GAP / (NODE_WIDTH + COLUMN_GAP);
    const ideal = Math.round(Math.sqrt(count * TARGET_ASPECT * cellAspect));
    return Math.min(Math.max(ideal, 1), MAX_COLUMNS);
};

const node = (
    id: string,
    x: number,
    y: number,
    data: ExplodeNodeData,
    zIndex = 0,
): Node<ExplodeNodeData> => ({ id, type: NodeKind.BUCKET, position: { x, y }, data, zIndex });

const titleEdge = (source: string, target: string, id: string): Edge => ({
    id,
    source,
    target,
    type: "default",
    zIndex: OVERLAY_Z - 1,
    style: { stroke: "#9775fa", strokeWidth: 1.6 },
});

const valueEdge = (source: string, target: string, id: string): Edge => ({
    id,
    source,
    target,
    type: "default",
    zIndex: OVERLAY_Z - 1,
    style: { stroke: "#d0bfff", strokeWidth: 1 },
});

const buildFieldGrid = (
    fieldOptions: NonNullable<BuildFacetGraphParams["fieldOptions"]>,
    rootLabel: string,
    gridPage: number,
): ExplodeFlow => {
    const start = gridPage * OVERLAY_PAGE_SIZE;
    const pageOptions = fieldOptions.slice(start, start + OVERLAY_PAGE_SIZE);
    const remaining = fieldOptions.length - (start + pageOptions.length);
    const cellCount = pageOptions.length + (remaining > 0 ? 1 : 0) + (gridPage > 0 ? 1 : 0);

    const columns = columnsForAspect(cellCount || 1);
    const stepX = NODE_WIDTH + COLUMN_GAP;
    const gridWidth = Math.min(cellCount || 1, columns) * stepX - COLUMN_GAP;
    const gridCenterX = (gridWidth - NODE_WIDTH) / 2;

    const fieldNodes = pageOptions.map((option, index) =>
        node(
            `f-${index}`,
            (index % columns) * stepX,
            GRID_TOP + Math.floor(index / columns) * ROW_GAP,
            {
                label: option.label,
                count: option.count,
                role: NodeRole.GROUP,
                categoryField: option.field,
                selection: [],
            },
        ),
    );

    const pagerNodes: Node<ExplodeNodeData>[] = [];
    if (remaining > 0) {
        const nextIndex = pageOptions.length;
        pagerNodes.push(
            node(
                "grid-next",
                (nextIndex % columns) * stepX,
                GRID_TOP + Math.floor(nextIndex / columns) * ROW_GAP,
                {
                    label: `…next ${Math.min(OVERLAY_PAGE_SIZE, remaining)} (${remaining.toLocaleString()} left)`,
                    role: NodeRole.GROUP,
                    selection: [],
                    muted: true,
                    gridPage: gridPage + 1,
                },
            ),
        );
    }
    if (gridPage > 0) {
        const backIndex = pageOptions.length + (remaining > 0 ? 1 : 0);
        pagerNodes.push(
            node(
                "grid-back",
                (backIndex % columns) * stepX,
                GRID_TOP + Math.floor(backIndex / columns) * ROW_GAP,
                {
                    label: "…back",
                    role: NodeRole.GROUP,
                    selection: [],
                    muted: true,
                    gridPage: gridPage - 1,
                },
            ),
        );
    }

    const rootNode = node("root", gridCenterX, ROOT_Y, {
        label: rootLabel,
        role: NodeRole.ROOT,
        selection: [],
    });

    const fieldEdges = pageOptions.map<Edge>((_, index) => ({
        id: `e-root-f${index}`,
        source: "root",
        target: `f-${index}`,
        type: "default",
        style: { stroke: "#c0b6e8", strokeWidth: 1 },
    }));

    return {
        nodes: [rootNode, ...fieldNodes, ...pagerNodes],
        edges: fieldEdges,
    };
};

const rowTops = (centerY: number): [number, number] => {
    const rowZeroY = centerY + ZONE_TOP_OFFSET;
    return [rowZeroY, rowZeroY + TITLE_SLOT_HEIGHT + ZONE_ROW_GAP];
};

const zoneTitleNode = (
    cluster: Cluster,
    index: number,
    centerX: number,
    tops: [number, number],
): Node<ExplodeNodeData> => {
    const col = Math.floor(index / ZONE_ROWS);
    const row = index % ZONE_ROWS;
    return node(
        `ttl-${cluster.field}`,
        centerX + ZONE_LEFT_OFFSET + col * ZONE_WIDTH,
        tops[row],
        {
            label: cluster.label,
            role: NodeRole.TITLE,
            count: cluster.distinctCount,
            title: true,
            overlay: { field: cluster.field, label: cluster.label },
            selection: [],
        },
        OVERLAY_Z,
    );
};

const buildFieldFocus = (
    categories: BuildFacetGraphParams["categories"],
    categoryField: string,
    categoryLabel: string,
    rootLabel: string,
): ExplodeFlow => {
    const stepX = NODE_WIDTH + COLUMN_GAP;
    const rootNode = node("root", 0, ROOT_Y, {
        label: rootLabel,
        role: NodeRole.ROOT,
        selection: [],
    });
    const fieldNode = node(
        "field-focus",
        stepX,
        ROOT_Y,
        {
            label: categoryLabel,
            role: NodeRole.TITLE,
            title: true,
            focused: true,
            count: categories.length,
            selection: [],
        },
        OVERLAY_Z,
    );

    const centerX = stepX;
    const tops = rowTops(ROOT_Y);
    const topValues = categories.slice(0, FIELD_FOCUS_TOP_VALUES);
    const branchNodes = topValues.map((bucket, index) => {
        const col = Math.floor(index / ZONE_ROWS);
        const row = index % ZONE_ROWS;
        return node(
            `fv-${index}`,
            centerX + ZONE_LEFT_OFFSET + col * ZONE_WIDTH,
            tops[row],
            {
                label: bucket.value,
                count: bucket.count,
                role: NodeRole.GROUP,
                selection: [{ field: categoryField, value: bucket.value }],
            },
            OVERLAY_Z,
        );
    });

    const showAllNode =
        categories.length > topValues.length
            ? node(
                  "fv-all",
                  centerX +
                      ZONE_LEFT_OFFSET +
                      Math.floor(topValues.length / ZONE_ROWS) * ZONE_WIDTH,
                  tops[topValues.length % ZONE_ROWS],
                  {
                      label: `…show all ${categories.length.toLocaleString()} values`,
                      role: NodeRole.GROUP,
                      selection: [],
                      muted: true,
                      overlay: { field: categoryField, label: categoryLabel },
                      overlayPage: 0,
                  },
                  OVERLAY_Z,
              )
            : undefined;

    const branchAndAll = showAllNode ? [...branchNodes, showAllNode] : branchNodes;
    const rootEdge = titleEdge("root", "field-focus", "e-root-field");
    const branchEdges = branchAndAll.map((branch, index) =>
        titleEdge("field-focus", branch.id, `e-fv-${index}`),
    );

    return {
        nodes: [rootNode, fieldNode, ...branchAndAll],
        edges: [rootEdge, ...branchEdges],
    };
};

const buildOverlay = (overlay: OverlayData, params: BuildFacetGraphParams): ExplodeFlow => {
    const { categoryField, categoryLabel, rootLabel, selected, categories } = params;
    const start = overlay.page * OVERLAY_PAGE_SIZE;
    const pageBuckets = overlay.buckets.slice(start, start + OVERLAY_PAGE_SIZE);
    const remaining = overlay.buckets.length - (start + pageBuckets.length);
    const target = { field: overlay.field, label: overlay.label };

    const cellCount = pageBuckets.length + (remaining > 0 ? 1 : 0) + (overlay.page > 0 ? 1 : 0);
    const columns = columnsForAspect(cellCount || 1);
    const stepX = NODE_WIDTH + COLUMN_GAP;
    const cellX = (index: number) => (index % columns) * stepX;
    const cellY = (index: number) => GRID_TOP + Math.floor(index / columns) * ROW_GAP;

    const selectionFor = (value: string): BucketSelection[] =>
        overlay.field === categoryField || !selected
            ? [{ field: overlay.field, value }]
            : [selected, { field: overlay.field, value }];

    const valueNodes = pageBuckets.map((bucket, index) =>
        node(
            `ovv-${index}`,
            cellX(index),
            cellY(index),
            {
                label: bucket.value,
                count: bucket.count,
                role: NodeRole.GROUP,
                selection: selectionFor(bucket.value),
            },
            STAGE_Z,
        ),
    );

    const nextNode =
        remaining > 0
            ? node(
                  "ov-next",
                  cellX(pageBuckets.length),
                  cellY(pageBuckets.length),
                  {
                      label: `…next ${Math.min(OVERLAY_PAGE_SIZE, remaining)} (${remaining.toLocaleString()} left)`,
                      role: NodeRole.GROUP,
                      selection: [],
                      muted: true,
                      overlay: target,
                      overlayPage: overlay.page + 1,
                  },
                  STAGE_Z,
              )
            : undefined;

    const backIndex = pageBuckets.length + (nextNode ? 1 : 0);
    const backNode =
        overlay.page > 0
            ? node(
                  "ov-back",
                  cellX(backIndex),
                  cellY(backIndex),
                  {
                      label: "…back",
                      role: NodeRole.GROUP,
                      selection: [],
                      muted: true,
                      overlay: target,
                      overlayPage: overlay.page - 1,
                  },
                  STAGE_Z,
              )
            : undefined;

    const gridWidth = Math.min(cellCount || 1, columns) * stepX - COLUMN_GAP;
    const headerX = (gridWidth - NODE_WIDTH) / 2;
    const headerNode = node(
        "ov-hdr",
        headerX,
        ROOT_Y,
        {
            label: overlay.label,
            role: NodeRole.TITLE,
            title: true,
            focused: true,
            count: overlay.buckets.length,
            selection: [],
        },
        STAGE_Z,
    );

    const chainValue = selected && overlay.field !== categoryField ? selected : undefined;
    const chainLength = chainValue ? 2 : 1;
    const rootChainNode = node(
        "ov-root",
        headerX - chainLength * stepX,
        ROOT_Y,
        {
            label: rootLabel,
            role: NodeRole.ROOT,
            selection: [],
        },
        STAGE_Z,
    );
    const selectedChainNode = chainValue
        ? node(
              "ov-sel",
              headerX - stepX,
              ROOT_Y,
              {
                  label: chainValue.value,
                  caption: categoryLabel,
                  count: categories.find((bucket) => bucket.value === chainValue.value)?.count,
                  role: NodeRole.GROUP,
                  focused: true,
                  selection: [chainValue],
              },
              STAGE_Z,
          )
        : undefined;

    const chainEdges = (
        chainValue
            ? [titleEdge("ov-root", "ov-sel", "e-ovc-0"), titleEdge("ov-sel", "ov-hdr", "e-ovc-1")]
            : [titleEdge("ov-root", "ov-hdr", "e-ovc-0")]
    ).map((edge) => ({ ...edge, zIndex: STAGE_Z - 1 }));

    const spiderEdges = valueNodes.map((valueNode, index) => ({
        ...valueEdge("ov-hdr", valueNode.id, `e-ov-${index}`),
        zIndex: STAGE_Z - 1,
    }));

    const chainNodes = selectedChainNode ? [rootChainNode, selectedChainNode] : [rootChainNode];
    const pagerNodes = [nextNode, backNode].filter(
        (entry): entry is Node<ExplodeNodeData> => entry !== undefined,
    );

    return {
        nodes: [...chainNodes, headerNode, ...valueNodes, ...pagerNodes],
        edges: [...chainEdges, ...spiderEdges],
    };
};

const buildBaseGraph = ({
    categories,
    categoryField,
    categoryLabel,
    rootLabel,
    gridPage = 0,
    selected,
    clusters,
    subLoading = false,
    fieldOptions,
}: BuildFacetGraphParams): ExplodeFlow => {
    if (fieldOptions && !selected) {
        return buildFieldGrid(fieldOptions, rootLabel, gridPage);
    }

    if (!selected) {
        return buildFieldFocus(categories, categoryField, categoryLabel, rootLabel);
    }

    const stepX = NODE_WIDTH + COLUMN_GAP;
    const rootNode = node("root", 0, ROOT_Y, {
        label: rootLabel,
        role: NodeRole.ROOT,
        selection: [],
    });
    const centerId = "value-focus";
    const valueFocusNode = node(
        centerId,
        stepX,
        ROOT_Y,
        {
            label: selected.value,
            caption: categoryLabel,
            count: categories.find((bucket) => bucket.value === selected.value)?.count,
            role: NodeRole.GROUP,
            focused: true,
            selection: [selected],
        },
        OVERLAY_Z,
    );
    const centerX = stepX;
    const centerY = ROOT_Y;
    const rootEdge = titleEdge("root", centerId, "e-root-value");

    const showGhosts = subLoading && clusters.length === 0;
    if (showGhosts) {
        const ghostRowTops: [number, number] = [
            centerY + ZONE_TOP_OFFSET,
            centerY + ZONE_TOP_OFFSET + TITLE_SLOT_HEIGHT + ZONE_ROW_GAP,
        ];
        const ghostNodes = Array.from({ length: GHOST_COUNT }, (_, index) => {
            const col = Math.floor(index / GHOST_ZONE_SLOTS);
            const row = index % GHOST_ZONE_SLOTS;
            return node(
                `g-${index}`,
                centerX + ZONE_LEFT_OFFSET + col * ZONE_WIDTH,
                ghostRowTops[row],
                {
                    label: "",
                    role: NodeRole.GROUP,
                    selection: [],
                    skeleton: true,
                },
                OVERLAY_Z,
            );
        });
        const ghostEdges = ghostNodes.map((ghost, index) =>
            titleEdge(centerId, ghost.id, `e-ghost-${index}`),
        );
        return {
            nodes: [rootNode, valueFocusNode, ...ghostNodes],
            edges: [rootEdge, ...ghostEdges],
        };
    }

    const tops = rowTops(centerY);
    const titleNodes = clusters.map((cluster, zoneIndex) =>
        zoneTitleNode(cluster, zoneIndex, centerX, tops),
    );
    const zoneEdges = titleNodes.map((titleNode, zoneIndex) =>
        titleEdge(centerId, titleNode.id, `e-ttl-${zoneIndex}`),
    );

    return {
        nodes: [rootNode, valueFocusNode, ...titleNodes],
        edges: [rootEdge, ...zoneEdges],
    };
};

export const buildFacetGraph = (params: BuildFacetGraphParams): ExplodeFlow => {
    const base = buildBaseGraph(params);
    if (!params.overlay) return base;

    const dimmedNodes = base.nodes.map((baseNode) => ({
        ...baseNode,
        data: { ...baseNode.data, dimmed: true, focused: false },
    }));
    const fadedEdges = base.edges.map((edge) => ({
        ...edge,
        style: { stroke: "#f4f0fc", strokeWidth: 1 },
    }));

    const overlayFlow = buildOverlay(params.overlay, params);
    return {
        nodes: [...dimmedNodes, ...overlayFlow.nodes],
        edges: [...fadedEdges, ...overlayFlow.edges],
    };
};
